import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiDollarSign, FiLoader, FiMove, FiX } from 'react-icons/fi';
import { api } from '../services/api';
import AdminTranslationPopoverPortal from './AdminTranslationPopoverPortal';
import './AdminMarketPrice.css';

const printableAmount = (value) => (
  value === null || value === undefined ? '' : String(value)
);

const AdminMarketPrice = ({
  entityType,
  entityId,
  field = 'price',
  fieldLabel = 'قیمت',
  sourceValue,
  groupId = '',
  planId = '',
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locales, setLocales] = useState([]);
  const [prices, setPrices] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');
  const [value, setValue] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const timerRef = useRef(null);
  const requestRef = useRef(0);

  const marketLocales = useMemo(
    () => locales.filter((locale) => locale.enabled !== false && locale.code !== 'fa'),
    [locales]
  );
  const activeMarket = marketLocales.find((locale) => locale.code === activeLocale);

  const amountFor = (localeCode, items = prices) => {
    if (entityType === 'product') {
      return items.find((item) => item.locale === localeCode)?.[field];
    }
    return items.find((item) => (
      String(item.groupId) === String(groupId) && String(item.planId) === String(planId)
    ))?.marketPrices?.find((item) => item.locale === localeCode)?.amount;
  };

  const selectLocale = (localeCode, items = prices) => {
    setActiveLocale(localeCode);
    setValue(printableAmount(amountFor(localeCode, items)));
    setSaveState('idle');
    setError('');
  };

  const load = async () => {
    const pricePath = entityType === 'product'
      ? `/api/products/${entityId}/market-prices`
      : `/api/services/${entityId}/market-prices`;
    const [localeResponse, priceResponse] = await Promise.all([
      api.get('/api/locales/admin'),
      api.get(pricePath),
    ]);
    const localeItems = Array.isArray(localeResponse.data) ? localeResponse.data : [];
    const priceItems = entityType === 'product'
      ? (Array.isArray(priceResponse.data?.marketPrices) ? priceResponse.data.marketPrices : [])
      : (Array.isArray(priceResponse.data) ? priceResponse.data : []);
    setLocales(localeItems);
    setPrices(priceItems);
    const first = localeItems.find((item) => item.enabled !== false && item.code !== 'fa')?.code || '';
    selectLocale(first, priceItems);
  };

  const toggle = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) return setOpen(false);
    setOpen(true);
    setLoading(true);
    setError('');
    try {
      await load();
    } catch (loadError) {
      setError(loadError.data?.message || loadError.message || 'دریافت قیمت بازارها انجام نشد');
    } finally {
      setLoading(false);
    }
    return undefined;
  };

  const save = (nextValue, localeCode) => {
    setValue(nextValue);
    setSaveState('waiting');
    setError('');
    if (timerRef.current) clearTimeout(timerRef.current);
    requestRef.current += 1;
    const requestNumber = requestRef.current;
    timerRef.current = setTimeout(async () => {
      setSaveState('saving');
      try {
        if (entityType === 'product') {
          await api.patch(`/api/products/${entityId}/market-prices/${localeCode}`, {
            field,
            value: nextValue,
          });
        } else {
          await api.patch(
            `/api/services/${entityId}/market-prices/${localeCode}/${groupId}/${planId}`,
            { amount: nextValue }
          );
        }
        if (requestRef.current === requestNumber) setSaveState('saved');
        const numeric = nextValue === '' ? null : Number(nextValue);
        setPrices((items) => {
          if (entityType === 'product') {
            const existing = items.find((item) => item.locale === localeCode);
            if (field === 'price' && nextValue === '') {
              return items.filter((item) => item.locale !== localeCode);
            }
            const updated = { ...(existing || { locale: localeCode }), [field]: numeric };
            return existing
              ? items.map((item) => (item === existing ? updated : item))
              : [...items, updated];
          }
          return items.map((plan) => {
            if (String(plan.groupId) !== String(groupId) || String(plan.planId) !== String(planId)) return plan;
            const remaining = (plan.marketPrices || []).filter((item) => item.locale !== localeCode);
            return {
              ...plan,
              marketPrices: nextValue === ''
                ? remaining
                : [...remaining, { locale: localeCode, amount: numeric }],
            };
          });
        });
      } catch (saveError) {
        if (requestRef.current === requestNumber) {
          setSaveState('error');
          setError(saveError.data?.message || saveError.message || 'ذخیره قیمت انجام نشد');
        }
      }
    }, 650);
  };

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  if (!entityId || !['product', 'service'].includes(entityType)) return null;

  return (
    <span className="admin-market-price" ref={rootRef}>
      <button type="button" className={open ? 'is-active' : ''} onClick={toggle} title={`قیمت بازارهای «${fieldLabel}»`}>
        <FiDollarSign /><small>{marketLocales.length || ''}</small>
      </button>
      {open && (
        <AdminTranslationPopoverPortal
          anchorRef={rootRef}
          className="admin-market-price__popover"
          ariaLabel={`${fieldLabel} در بازارهای دیگر`}
          onClose={() => setOpen(false)}
        >
          {({ dragHandleProps }) => <>
          <span className="admin-market-price__head" {...dragHandleProps}>
            <span>
              <strong>{fieldLabel} در بازارهای دیگر</strong>
              <small>هر مبلغ مستقل است و تبدیل خودکار انجام نمی‌شود.</small>
              <small className="admin-translation-popover__drag-hint"><FiMove aria-hidden="true" /> برای جابه‌جایی بکشید</small>
            </span>
            <button type="button" className="ui-close-button" onClick={() => setOpen(false)} aria-label="بستن"><FiX /></button>
          </span>
          {loading ? <span className="admin-market-price__loading"><FiLoader /> در حال دریافت...</span> : (
            <>
              <span className="admin-market-price__tabs">
                {marketLocales.map((locale) => (
                  <button
                    type="button"
                    key={locale.code}
                    className={activeLocale === locale.code ? 'is-active' : ''}
                    onClick={() => selectLocale(locale.code)}
                  >
                    <b>{locale.code.toUpperCase()}</b> {locale.nativeName}
                    {amountFor(locale.code) !== null && amountFor(locale.code) !== undefined ? <FiCheck /> : null}
                  </button>
                ))}
              </span>
              {activeMarket && (
                <span className="admin-market-price__editor" dir={activeMarket.direction || 'rtl'}>
                  <small dir="rtl">قیمت فارسی: {printableAmount(sourceValue) || '—'} تومان</small>
                  <label>
                    <span>{activeMarket.currencyLabel} ({activeMarket.currencyCode})</span>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={value}
                      placeholder="برای قیمت‌گذاری وارد کنید؛ خالی یعنی ناموجود"
                      onChange={(event) => save(event.target.value, activeLocale)}
                    />
                  </label>
                  <small className={activeMarket.paymentReady ? 'is-ready' : 'is-pending'} dir="rtl">
                    {activeMarket.paymentReady
                      ? `درگاه ${activeMarket.paymentProvider} فعال است.`
                      : activeMarket.paymentEnabled && activeMarket.paymentProvider !== 'none'
                        ? 'تنظیم درگاه ذخیره شده، اما آداپتور این واحد پول هنوز در کد متصل نشده است.'
                        : 'فعلاً فقط نمایش قیمت و ثبت درخواست فعال است؛ درگاه آنلاین این بازار بعداً متصل می‌شود.'}
                  </small>
                  <small className={`admin-market-price__status is-${saveState}`} dir="rtl">
                    {saveState === 'waiting' && 'در انتظار توقف تایپ...'}
                    {saveState === 'saving' && 'در حال ذخیره...'}
                    {saveState === 'saved' && 'قیمت ذخیره شد.'}
                    {saveState === 'error' && 'ذخیره انجام نشد.'}
                  </small>
                </span>
              )}
            </>
          )}
          {error && <small className="admin-market-price__error">{error}</small>}
          </>}
        </AdminTranslationPopoverPortal>
      )}
    </span>
  );
};

export default AdminMarketPrice;
